import * as React from "react";
import { Archive, Building2, ChevronDown, ChevronRight, Factory, Plus, Search } from "lucide-react";
import { useNavigate } from "react-router";
import { useToggle } from "react-use";

import {
  useGetSupplierFamilyCategoriesQuery,
  useGetSupplierOwnerOptionsQuery,
  useGetSuppliersQuery,
} from "@/api/suppliers";
import { PROCUREMENT_STATUS_OPTIONS, SUPPLIER_CATEGORY_OPTIONS } from "@/api/suppliers.constraints";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import { useGetDirectoryUsersQuery } from "@/api/users-admin";
import { MultiSelectFilter } from "@/components/multi-select-filter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table/table";
import type {
  ProcurementStatus,
  SupplierCategory,
  SupplierDivision,
  SupplierSortDirection,
  SupplierSortField,
  SupplierStatus,
} from "@/types/supplier";

import { MissingGbpRateNote } from "./components/contract-family-enrichment";
import { formatContractValue } from "./components/contract-value-format";
import { CreateSupplierDialog } from "./components/create-supplier-dialog";
import { SupplierCategories } from "./components/supplier-categories";
import { SupplierFamiliesExpansion } from "./components/supplier-families-expansion";

const PAGE_SIZE = 20;

interface SortButtonProps {
  label: string;
  field: SupplierSortField;
  sortBy: SupplierSortField;
  sortDirection: SupplierSortDirection;
  onSort: (field: SupplierSortField) => void;
}

const SortButton = ({ label, field, sortBy, sortDirection, onSort }: SortButtonProps) => (
  <button type="button" onClick={() => onSort(field)}>
    {label} {sortBy === field ? (sortDirection === "asc" ? "↑" : "↓") : ""}
  </button>
);

// An empty filter is left out of the request rather than sent as an empty list.
const picked = <T,>(values: T[]) => (values.length > 0 ? values : undefined);

const capitalized = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

const ownerDisplayName = (owner: { name: string; surname: string; email: string }) =>
  [owner.name, owner.surname].filter(Boolean).join(" ") || owner.email;

export const SuppliersPage = () => {
  const navigate = useNavigate();
  const { organization } = useAuthenticatedUser();
  const [searchInput, setSearchInput] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [status, setStatus] = React.useState<SupplierStatus>("active");
  const [categories, setCategories] = React.useState<SupplierCategory[]>([]);
  const [ownerIds, setOwnerIds] = React.useState<string[]>([]);
  const [familyStatuses, setFamilyStatuses] = React.useState<ProcurementStatus[]>([]);
  const [familyCategories, setFamilyCategories] = React.useState<string[]>([]);
  const [divisions, setDivisions] = React.useState<SupplierDivision[]>([]);
  const [sortBy, setSortBy] = React.useState<SupplierSortField>("name");
  const [sortDirection, setSortDirection] = React.useState<SupplierSortDirection>("asc");
  const [page, setPage] = React.useState(1);
  const [expandedId, setExpandedId] = React.useState<string | null>(null);
  const [createOpen, toggleCreateOpen] = useToggle(false);

  React.useEffect(() => {
    const timeout = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [searchInput]);

  const { data, isLoading, isFetching, isError, refetch } = useGetSuppliersQuery({
    search: search || undefined,
    status,
    category: picked(categories),
    owner_user_id: picked(ownerIds),
    family_status: picked(familyStatuses),
    family_category: picked(familyCategories),
    division: picked(divisions),
    sort_by: sortBy,
    sort_direction: sortDirection,
    offset: (page - 1) * PAGE_SIZE,
    limit: PAGE_SIZE,
  });
  const { data: owners = [] } = useGetSupplierOwnerOptionsQuery();
  const { data: familyCategoryNames = [] } = useGetSupplierFamilyCategoriesQuery();
  const sortedOwners = React.useMemo(
    () =>
      [...owners].sort(
        (left, right) =>
          ownerDisplayName(left).localeCompare(ownerDisplayName(right), undefined, { sensitivity: "base" }) ||
          left.id.localeCompare(right.id),
      ),
    [owners],
  );
  const displayOwnerIds = React.useMemo(
    () => [
      ...new Set((data?.items.map(supplier => supplier.owner_user_id) ?? []).filter((id): id is string => Boolean(id))),
    ],
    [data?.items],
  );
  const { data: displayOwners = [] } = useGetDirectoryUsersQuery(displayOwnerIds, {
    skip: displayOwnerIds.length === 0,
  });
  const ownersById = React.useMemo(() => new Map(displayOwners.map(owner => [owner.id, owner])), [displayOwners]);
  const familyCategoryOptions = React.useMemo(
    () => familyCategoryNames.map(value => ({ value, label: value })),
    [familyCategoryNames],
  );
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE));

  const changeSort = (field: SupplierSortField) => {
    if (sortBy === field) setSortDirection(current => (current === "asc" ? "desc" : "asc"));
    else {
      setSortBy(field);
      setSortDirection("asc");
    }
    setPage(1);
  };
  const sortProps = { sortBy, sortDirection, onSort: changeSort };

  return (
    <main
      className="-mx-4 -my-8 min-h-screen bg-[#f7f3ee] px-5 py-6 sm:-mx-6 lg:-mx-8 lg:px-8 lg:py-7"
      aria-label="Suppliers workspace"
    >
      <div className="mx-auto max-w-7xl">
        <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-desert-sand-700">
              <Factory className="size-4" aria-hidden="true" /> Supplier registry
            </div>
            <h1 className="text-3xl font-bold leading-tight text-[#111b35]">Suppliers</h1>
            <p className="mt-1 max-w-2xl text-sm font-medium text-gray-500">
              Manage legal entities, owners and their accessible Contract Families.
            </p>
          </div>
          <Button type="button" onClick={() => toggleCreateOpen(true)}>
            <Plus className="mr-2 size-4" aria-hidden="true" /> Create Supplier
          </Button>
        </header>

        <section
          className="mt-6 overflow-hidden rounded-lg border border-[#ddd5cb] bg-[#fffdfb]"
          aria-label="Suppliers registry"
        >
          <div className="flex gap-1 border-b border-[#e4ddd5] px-5 pt-3" role="tablist" aria-label="Supplier status">
            {(["active", "archived"] as const).map(tabStatus => (
              <button
                key={tabStatus}
                type="button"
                role="tab"
                aria-selected={status === tabStatus}
                className={`border-b-2 px-4 py-2.5 text-sm font-semibold capitalize transition-colors ${
                  status === tabStatus
                    ? "border-desert-sand-700 text-[#111b35]"
                    : "border-transparent text-gray-500 hover:text-[#111b35]"
                }`}
                onClick={() => {
                  setStatus(tabStatus);
                  setPage(1);
                  setExpandedId(null);
                }}
              >
                {tabStatus}
              </button>
            ))}
          </div>

          <div className="grid gap-3 border-b border-[#e4ddd5] px-5 py-4 md:grid-cols-2 xl:grid-cols-3">
            <label className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-500" />
              <Input
                aria-label="Search Suppliers"
                value={searchInput}
                onChange={event => setSearchInput(event.target.value)}
                placeholder="Search by Supplier or Contract Family name"
                className="border-[#ddd5cb] bg-white pl-9 focus-visible:ring-desert-sand-300"
              />
            </label>
            <MultiSelectFilter
              label="Supplier category"
              allLabel="All categories"
              values={categories}
              options={SUPPLIER_CATEGORY_OPTIONS.map(([value, label]) => ({ value, label }))}
              onChange={values => {
                setCategories(values);
                setPage(1);
                setExpandedId(null);
              }}
            />
            <select
              aria-label="Sort Suppliers"
              value={`${sortBy}:${sortDirection}`}
              onChange={event => {
                const [field, direction] = event.target.value.split(":") as [SupplierSortField, SupplierSortDirection];
                setSortBy(field);
                setSortDirection(direction);
                setPage(1);
              }}
              className="h-10 rounded-md border border-[#ddd5cb] bg-white px-3 text-sm font-medium text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-desert-sand-300"
            >
              <option value="name:asc">Name A–Z</option>
              <option value="name:desc">Name Z–A</option>
              <option value="accessible_family_count:desc">Most Contract Families</option>
              <option value="accessible_family_count:asc">Fewest Contract Families</option>
              <option value="total_contract_value:desc">Highest Total Contract Value</option>
              <option value="total_contract_value:asc">Lowest Total Contract Value</option>
            </select>
            <MultiSelectFilter
              label="Contract Family status"
              allLabel="All family statuses"
              values={familyStatuses}
              options={PROCUREMENT_STATUS_OPTIONS.map(value => ({ value, label: capitalized(value) }))}
              onChange={values => {
                setFamilyStatuses(values);
                setPage(1);
              }}
            />
            <MultiSelectFilter
              label="Contract Family category"
              allLabel="All family categories"
              values={familyCategories}
              options={familyCategoryOptions}
              onChange={values => {
                setFamilyCategories(values);
                setPage(1);
              }}
            />
            <MultiSelectFilter
              label="Contract Family division"
              allLabel="All divisions"
              values={divisions}
              options={(organization.contractFamilyDivisions ?? []).map(value => ({ value, label: value }))}
              onChange={values => {
                setDivisions(values);
                setPage(1);
              }}
            />
            <MultiSelectFilter
              label="Supplier owner"
              allLabel="All owners"
              values={ownerIds}
              options={sortedOwners.map(owner => ({ value: owner.id, label: ownerDisplayName(owner) }))}
              onChange={values => {
                setOwnerIds(values);
                setPage(1);
              }}
            />
          </div>

          {isLoading ? (
            <div className="p-10 text-center text-sm font-medium text-gray-500">Loading Suppliers…</div>
          ) : null}
          {isError ? (
            <div className="p-10 text-center">
              <p className="text-sm text-red-700">Suppliers could not be loaded.</p>
              <Button type="button" variant="outline" size="sm" className="mt-3" onClick={() => refetch()}>
                Try again
              </Button>
            </div>
          ) : null}
          {!isLoading && !isError && data?.items.length === 0 ? (
            <div className="flex flex-col items-center px-4 py-12 text-center">
              <span className="flex size-12 items-center justify-center rounded-full bg-[#f1e2ce] text-desert-sand-700">
                {status === "archived" ? <Archive className="size-5" /> : <Factory className="size-5" />}
              </span>
              <p className="mt-3 text-sm font-semibold text-[#111b35]">No {status} Suppliers found</p>
              <p className="mt-1 text-sm font-medium text-gray-500">Adjust the filters or create the first Supplier.</p>
            </div>
          ) : null}
          {data?.items.length ? (
            <Table className="min-w-[960px] table-fixed" containerClassName={isFetching ? "opacity-70" : undefined}>
              <TableHeader className="bg-[#f7f2ec]">
                <TableRow className="border-[#ebe5de] hover:bg-[#f7f2ec]">
                  <TableHead className="w-12 px-4">
                    <span className="sr-only">Expand</span>
                  </TableHead>
                  <TableHead className="px-3 text-[11px] font-semibold uppercase tracking-[0.08em]">
                    <SortButton label="Supplier" field="name" {...sortProps} />
                  </TableHead>
                  <TableHead className="px-3 text-[11px] font-semibold uppercase tracking-[0.08em]">
                    External ID
                  </TableHead>
                  <TableHead className="px-3 text-[11px] font-semibold uppercase tracking-[0.08em]">Owner</TableHead>
                  <TableHead className="px-3 text-right text-[11px] font-semibold uppercase tracking-[0.08em]">
                    <SortButton label="Total value" field="total_contract_value" {...sortProps} />
                  </TableHead>
                  <TableHead className="px-3 text-right text-[11px] font-semibold uppercase tracking-[0.08em]">
                    <SortButton label="Families" field="accessible_family_count" {...sortProps} />
                  </TableHead>
                  <TableHead className="px-4 text-[11px] font-semibold uppercase tracking-[0.08em]">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className="divide-y divide-[#ebe5de] bg-white">
                {data.items.map(supplier => {
                  const expanded = expandedId === supplier.id;
                  const owner = supplier.owner_user_id ? ownersById.get(supplier.owner_user_id) : undefined;
                  const ownerName = owner
                    ? [owner.name, owner.surname].filter(Boolean).join(" ") || owner.email
                    : supplier.owner_user_id
                      ? "Unknown user"
                      : "Unassigned";
                  return (
                    <React.Fragment key={supplier.id}>
                      <TableRow
                        className={
                          expanded ? "border-0 bg-[#fbf8f4] hover:bg-[#fbf8f4]" : "border-0 hover:bg-[#fbf8f4]"
                        }
                      >
                        <TableCell className="px-4 py-3">
                          {supplier.accessible_family_count > 0 ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              aria-label={`${expanded ? "Collapse" : "Expand"} ${supplier.name}`}
                              aria-expanded={expanded}
                              onClick={() => setExpandedId(current => (current === supplier.id ? null : supplier.id))}
                            >
                              {expanded ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
                            </Button>
                          ) : null}
                        </TableCell>
                        <TableCell className="px-3 py-3">
                          <button
                            type="button"
                            className="flex max-w-full items-center gap-3 text-left"
                            onClick={() => navigate(`/suppliers/${supplier.id}`)}
                          >
                            <span className="flex size-8 flex-shrink-0 items-center justify-center rounded-md bg-[#f1e2ce] text-desert-sand-700">
                              <Building2 className="size-4" />
                            </span>
                            <span className="truncate text-sm font-semibold text-gray-950 hover:underline">
                              {supplier.name}
                            </span>
                          </button>
                          <SupplierCategories supplier={supplier} />
                        </TableCell>
                        <TableCell className="px-3 py-3 font-medium text-gray-600">
                          {supplier.external_id || "—"}
                        </TableCell>
                        <TableCell className="px-3 py-3 font-medium text-gray-700">{ownerName}</TableCell>
                        <TableCell
                          aria-label={`${supplier.name} Total Contract Value`}
                          className="px-3 py-3 text-right font-semibold tabular-nums text-gray-700"
                        >
                          {supplier.estimated_total_contract_value_gbp
                            ? formatContractValue(supplier.estimated_total_contract_value_gbp, "GBP")
                            : "—"}
                          {!supplier.contract_value_gbp_complete ? <MissingGbpRateNote className="block" /> : null}
                        </TableCell>
                        <TableCell className="px-3 py-3 text-right font-semibold tabular-nums text-gray-700">
                          {supplier.accessible_family_count}
                        </TableCell>
                        <TableCell className="px-4 py-3">
                          <Badge
                            variant={supplier.status === "active" ? "traffic-green" : "secondary"}
                            className="capitalize"
                          >
                            {supplier.status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                      {expanded ? (
                        <TableRow className="border-0 hover:bg-white">
                          <TableCell colSpan={7} className="p-0">
                            <SupplierFamiliesExpansion
                              supplierId={supplier.id}
                              expanded
                              familyStatus={picked(familyStatuses)}
                              familyCategory={picked(familyCategories)}
                            />
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          ) : null}

          {data && data.total > 0 ? (
            <div className="flex flex-col gap-3 border-t border-[#e4ddd5] px-5 py-4 text-sm font-medium text-gray-600 sm:flex-row sm:items-center sm:justify-between">
              <span>
                {data.total} {data.total === 1 ? "Supplier" : "Suppliers"}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(current => current - 1)}
                >
                  Previous
                </Button>
                <span className="px-2 tabular-nums">
                  Page {page} of {totalPages}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(current => current + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          ) : null}
        </section>
      </div>

      <CreateSupplierDialog open={createOpen} onOpenChange={toggleCreateOpen} />
    </main>
  );
};
